import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { useStaffMe } from '../../hooks/useStaffMe';
import { InPageTabs } from '../../components/InPageTabs';
import { InstantTime } from '../../lib/time-format';
import { useZoneReadReport } from '../../components/ZoneContext';
import { ZoneCell, ZoneFilter } from '../../components/ZoneIndicators';
import { ActionIcon, ActionsRow } from '../../components/ActionIcon';
import { StaffApiError } from '../../lib/api-client';
import { ZONE_TABLE, consoleZoneLabel, HOME_ZONE, useWorkingZone, zoneByCode } from '../../lib/zones';
import {
  listBenchHistory, getBenchRun, startBenchRun,
  fmtPeakMemory, type BenchRun, type BenchFunction, type SlotResult, type ViewportKey, type Viewports, type Difference,
} from '../../lib/test-bench-api';
import './test-bench.css';

/**
 * Admin → COG → Test Bench (s3-grading-engine-node24, 1 November 2026).
 *
 * Jeff's rule: an upgrade is tested as a separate copy, from this page, BEFORE anything in production
 * changes. Each testable function has a live slot (what customers get) and a CANDIDATE slot (the
 * upgraded copy beside it). A run gives both the same input at the same time, in the zone the operator
 * is working in, and shows their answers side by side — what each ran on, whether it finished, how long
 * it took, and its grades, with differences highlighted. The bench records differences; it never
 * decides which answer is right.
 *
 * Built as a list of testable functions (the tabs), each with its own input form and result view, so
 * another function joins with an entry here and on the server. ONE entry today: the website grading
 * engine. super_admin only (test_bench:run) — every run spends AI calls on two engines.
 *
 * The run history is a read-only board (admin UI standards §6): every zone, a Zone column, a per-zone
 * legend and a zone filter on "All zones"; opening a run reads it from the run's own zone.
 */
const POLL_MS = 5000;
/** The runner's ceiling (15 minutes) plus a minute — a run not readable by then did not start. */
const STALE_MS = 16 * 60 * 1000;
const VIEWPORT_LABEL: Record<Viewports, string> = { both: 'Desktop and mobile', desktop: 'Desktop', mobile: 'Mobile' };
const VIEWPORT_KEYS: Record<Viewports, ViewportKey[]> = { both: ['desktop', 'mobile'], desktop: ['desktop'], mobile: ['mobile'] };
const FALLBACK_FUNCTIONS: BenchFunction[] = [
  { key: 'grading-engine', label: 'Website grading engine', live: 'nexa-effectiveness-engine', candidate: 'nexa-effectiveness-engine-candidate' },
];

const hostOf = (url: string) => { try { return new URL(url).hostname; } catch { return url; } };
const fmtDuration = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
};
const zoneOfRun = (run: BenchRun) => zoneByCode(run.zone) ?? HOME_ZONE;

/** The server's own plain-English refusal (400 / 409) when it sent one; else the shared mapper. */
function toastFor(err: unknown, title: string): { title: string; detail?: string } {
  if (err instanceof StaffApiError) {
    if (err.status === 403) return { title, detail: 'Only a super admin can use the Test Bench.' };
    const message = err.details?.message;
    if (typeof message === 'string' && message) return { title, detail: message };
  }
  return toastFromError(err, title);
}

function PassPill({ slot }: { slot: SlotResult | null }) {
  if (!slot) return <span className="tb-pill tb-pill--wait">Running…</span>;
  return slot.pass
    ? <span className="tb-pill tb-pill--pass" data-testid={`tb-${slot.slot}-pass`}>Passed</span>
    : <span className="tb-pill tb-pill--fail" data-testid={`tb-${slot.slot}-fail`}>Did not finish</span>;
}

function differs(diffs: Difference[], viewport: ViewportKey, what: Difference['what'], key?: string): boolean {
  return diffs.some((d) => d.viewport === viewport && d.what === what && (key === undefined || d.key === key));
}

/** The side-by-side result: one column per slot, rows aligned so a difference reads across. */
function RunResult({ run, fn }: { run: BenchRun; fn: BenchFunction }) {
  const slots: Array<{ label: string; name: string; r: SlotResult | null }> = [
    { label: 'Live', name: fn.live, r: run.live },
    { label: 'Candidate', name: fn.candidate, r: run.candidate },
  ];
  const vps = VIEWPORT_KEYS[run.input.viewports];
  const diffs = run.differences ?? [];
  const firstVp = (vp: ViewportKey) => (run.live?.viewports[vp] ?? run.candidate?.viewports[vp]);
  return (
    <section className="tb-result" aria-label="Result" data-testid="tb-result" data-status={run.status}>
      <header className="tb-result-head">
        <h2>{hostOf(run.input.url)}</h2>
        <p className="tb-muted">
          {VIEWPORT_LABEL[run.input.viewports]} · <ZoneCell zone={run.zone ?? HOME_ZONE.code} /> · started <InstantTime iso={run.startedAt} />
          {run.startedBy.email ? ` by ${run.startedBy.email}` : ''}
        </p>
        {run.status === 'complete' && (
          <p className={diffs.length ? 'tb-diff-summary tb-diff-summary--some' : 'tb-diff-summary'} data-testid="tb-diff-summary">
            {diffs.length === 0
              ? 'The two engines gave the same grades.'
              : `${diffs.length} difference${diffs.length === 1 ? '' : 's'} between the engines — highlighted below. Recorded, not judged.`}
          </p>
        )}
        {run.status === 'failed' && <p className="tb-fail-line" role="alert">{run.error ?? 'This run did not finish.'}</p>}
      </header>
      <table className="tb-compare" data-testid="tb-compare">
        <colgroup><col className="tb-col-label" /><col /><col /></colgroup>
        <thead>
          <tr><th scope="col"><span className="portal-visually-hidden">What</span></th>{slots.map((s) => <th scope="col" key={s.label}>{s.label}<span className="tb-fn">{s.name}</span></th>)}</tr>
        </thead>
        <tbody>
          <tr><th scope="row">Result</th>{slots.map((s) => <td key={s.label}><PassPill slot={s.r} /></td>)}</tr>
          <tr><th scope="row">Runtime</th>{slots.map((s) => <td key={s.label} data-testid={`tb-${s.label.toLowerCase()}-runtime`}>{s.r?.runtime ?? '—'}{s.r?.node ? <span className="tb-muted"> · Node {s.r.node.replace(/^v/, '')}</span> : null}</td>)}</tr>
          <tr><th scope="row">Browser</th>{slots.map((s) => (
            <td key={s.label} data-testid={`tb-${s.label.toLowerCase()}-browser`}>
              {s.r?.browser ?? '—'}
              {s.r?.browserSource && <span className="tb-muted"> · {s.r.browserSource === 'reported' ? 'reported by the engine' : 'installed by the deploy'}</span>}
            </td>
          ))}</tr>
          <tr><th scope="row">Time taken</th>{slots.map((s) => <td key={s.label}>{s.r ? fmtDuration(s.r.durationMs) : '—'}</td>)}</tr>
          <tr><th scope="row">Peak memory</th>{slots.map((s) => <td key={s.label} data-testid={`tb-${s.label.toLowerCase()}-memory`}>{fmtPeakMemory(s.r)}</td>)}</tr>
          <tr><th scope="row">AI checks</th>{slots.map((s) => <td key={s.label}>{s.r ? `${s.r.aiCalls} call${s.r.aiCalls === 1 ? '' : 's'}${s.r.aiFailedKeys ? ` · ${s.r.aiFailedKeys} could not be scored` : ''}` : '—'}</td>)}</tr>
          <tr><th scope="row">Problem</th>{slots.map((s) => <td key={s.label} className={s.r?.error ? 'tb-error' : undefined}>{s.r?.error ?? (s.r ? 'None' : '—')}</td>)}</tr>
          {vps.map((vp) => {
            const shape = firstVp(vp);
            return (
              <Fragment key={vp}>
                <tr className="tb-section"><th scope="rowgroup" colSpan={3}>{vp === 'desktop' ? 'Desktop' : 'Mobile'}</th></tr>
                <tr className={differs(diffs, vp, 'grade') || differs(diffs, vp, 'total') ? 'tb-row-diff' : undefined}>
                  <th scope="row">Grade</th>
                  {slots.map((s) => {
                    const v = s.r?.viewports[vp];
                    return <td key={s.label} data-testid={`tb-${s.label.toLowerCase()}-${vp}-grade`}>{v ? <><strong className="tb-grade">{v.grade}</strong> {v.total} / 100</> : '—'}</td>;
                  })}
                </tr>
                {(shape?.categories ?? []).map((c) => (
                  <tr key={c.key} className={differs(diffs, vp, 'category', c.key) ? 'tb-row-diff' : undefined}>
                    <th scope="row" className="tb-cat">{c.name}</th>
                    {slots.map((s) => {
                      const sc = s.r?.viewports[vp]?.categories.find((x) => x.key === c.key);
                      return <td key={s.label}>{sc ? `${sc.score} / ${sc.max}` : '—'}</td>;
                    })}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {vps.length === 1 && <p className="tb-muted tb-note">The engine always grades desktop and mobile; this shows the {vps[0]} grades you asked for.</p>}
    </section>
  );
}

function RunInFlight({ run }: { run: BenchRun }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const elapsed = now - Date.parse(run.startedAt);
  return (
    <section className="tb-inflight" role="status" aria-live="polite" data-testid="tb-inflight">
      <div className="tb-skeleton-grid" aria-hidden="true">
        {[0, 1].map((i) => <div key={i} className="tb-skeleton-col"><div className="tb-skeleton tb-skeleton--head" /><div className="tb-skeleton" /><div className="tb-skeleton" /><div className="tb-skeleton" /></div>)}
      </div>
      <p className="tb-inflight-text">
        Running the live engine and the candidate on <strong>{hostOf(run.input.url)}</strong> in <ZoneCell zone={run.zone ?? HOME_ZONE.code} /> — {fmtDuration(Math.max(0, elapsed))} so far.
        Grading takes a few minutes; this page keeps checking until both have answered.
      </p>
    </section>
  );
}

export function TestBenchPage() {
  const toast = useToast();
  const me = useStaffMe();
  const workingZone = useWorkingZone();
  const isSuper = me.status === 'loaded' && me.me.role === 'super_admin';

  const [functions, setFunctions] = useState<BenchFunction[]>(FALLBACK_FUNCTIONS);
  const [active, setActive] = useState<BenchFunction['key']>('grading-engine');
  const [runs, setRuns] = useState<BenchRun[] | null>(null);
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [url, setUrl] = useState('');
  const [viewports, setViewports] = useState<Viewports>('both');
  const [starting, setStarting] = useState(false);
  const [shown, setShown] = useState<BenchRun | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fn = functions.find((f) => f.key === active) ?? FALLBACK_FUNCTIONS[0]!;

  const loadHistory = useCallback(async () => {
    try {
      const h = await listBenchHistory();
      if (h.functions.length) setFunctions(h.functions);
      setRuns(h.runs);
      setFailedZones(h.failedZones);
    } catch (e) {
      setRuns([]);
      toast.push({ variant: 'error', ...toastFor(e, 'Couldn’t load the Test Bench history') });
    }
  }, [toast]);
  useEffect(() => { if (isSuper) void loadHistory(); }, [isSuper, loadHistory]);

  // Poll the shown run until it is finished — however long grading takes; the page never times out.
  useEffect(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    if (!shown || shown.status !== 'running') return;
    pollRef.current = setTimeout(async () => {
      try {
        const next = await getBenchRun(zoneOfRun(shown), shown.runId);
        setShown(next);
        if (next.status !== 'running') {
          void loadHistory();
          if (next.status === 'failed') toast.push({ variant: 'error', title: 'The test did not finish', detail: next.error ?? undefined });
        }
      } catch {
        // Not found yet (the runner records the run within a second of starting) or a missed poll:
        // retried on the next tick, not reported — unless the run is past the runner's own ceiling.
        if (Date.now() - Date.parse(shown.startedAt) > STALE_MS) {
          setShown({ ...shown, status: 'failed', error: 'This run did not start. Try it again.' });
        } else {
          setShown({ ...shown });
        }
      }
    }, POLL_MS);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [shown, loadHistory, toast]);

  const run = async (e: React.FormEvent) => {
    e.preventDefault();
    setStarting(true);
    try {
      const started = await startBenchRun(workingZone, { function: fn.key, url, viewports });
      setShown(started);
      void loadHistory();
    } catch (err) {
      toast.push({ variant: 'error', ...toastFor(err, 'Couldn’t start the test') });
    } finally {
      setStarting(false);
    }
  };

  const openRun = async (r: BenchRun) => {
    try { setShown(await getBenchRun(zoneOfRun(r), r.runId)); }
    catch (err) { toast.push({ variant: 'error', ...toastFor(err, 'Couldn’t open that run') }); }
  };

  useZoneReadReport({ rows: runs, failedZones, filter: zoneFilter });
  const all = useMemo(() => (runs ?? []).filter((r) => r.functionKey === active), [runs, active]);
  const legend = useMemo(() => ZONE_TABLE.map((z) => ({ code: z.code, n: all.filter((r) => (r.zone ?? HOME_ZONE.code) === z.code).length })), [all]);
  const listed = all.filter((r) => zoneFilter === 'all' || (r.zone ?? HOME_ZONE.code) === zoneFilter);
  const busy = shown?.status === 'running';

  return (
    <div className="alerts-page tb-page" data-testid="test-bench-page">
      <h1>Test Bench</h1>
      <div className="alerts-crumb">Admin → COG → Test Bench &nbsp;·&nbsp; test an upgraded copy of a function against the live one, before anything in production changes</div>
      <p className="tb-intro">
        Each function here has a <strong>live</strong> version — the one customers get — and a <strong>candidate</strong>: the upgraded copy, deployed beside it.
        A test gives both the same input at the same time and shows their answers side by side. Nothing a customer sees changes, and no report is made.
      </p>

      {me.status === 'loading' ? (
        <div role="status" aria-live="polite"><div className="tb-skeleton" aria-hidden="true" /><p className="tb-muted">Loading the Test Bench…</p></div>
      ) : me.status === 'error' ? (
        <div className="tb-locked" role="alert" data-testid="tb-access-error">
          <h2>Couldn’t check your access</h2>
          <p>Reload the page to try again.</p>
        </div>
      ) : !isSuper ? (
        <div className="tb-locked" role="note" data-testid="tb-locked">
          <h2>Only a super admin can use the Test Bench</h2>
          <p>Every test runs two engines and spends AI calls on both, so running tests and reading their results is limited to super admins.</p>
        </div>
      ) : (
        <>
          <InPageTabs tabs={functions.map((f) => ({ key: f.key, label: f.label }))} active={active} onChange={setActive} ariaLabel="Testable functions" />

          <form className="tb-form" onSubmit={(e) => void run(e)} data-testid="tb-form">
            <div className="tb-field tb-field--url">
              <label htmlFor="tb-url">Website address</label>
              <input id="tb-url" type="text" inputMode="url" autoComplete="off" placeholder="example.com" value={url} onChange={(e) => setUrl(e.target.value)} required disabled={busy} />
            </div>
            <fieldset className="tb-field tb-viewports" disabled={busy}>
              <legend>Grade</legend>
              {(['both', 'desktop', 'mobile'] as Viewports[]).map((v) => (
                <label key={v} className="tb-radio">
                  <input type="radio" name="tb-viewports" value={v} checked={viewports === v} onChange={() => setViewports(v)} /> {VIEWPORT_LABEL[v]}
                </label>
              ))}
            </fieldset>
            <button type="submit" className="tb-run" disabled={starting || busy || !url.trim()} data-testid="tb-run">{starting ? 'Starting…' : 'Run'}</button>
            <p className="tb-muted tb-where">
              Runs in <strong>{consoleZoneLabel(workingZone.code)}</strong> — the zone you are working in — on <span className="tb-fn">{fn.live}</span> and <span className="tb-fn">{fn.candidate}</span>.
            </p>
          </form>

          {shown && (shown.status === 'running' ? <RunInFlight run={shown} /> : <RunResult run={shown} fn={fn} />)}

          <h2 className="tb-history-title">Recent tests</h2>
          {failedZones.length > 0 && <div className="error-banner" role="alert">{failedZones.join(', ')} did not respond — its tests are missing from this list.</div>}
          <table className="alerts-zone-legend" data-testid="tb-zone-legend" aria-label="Tests by zone">
            <thead><tr><th scope="col">Zone</th><th scope="col">Tests</th></tr></thead>
            <tbody>{legend.map((r) => <tr key={r.code} data-zone-row={r.code}><th scope="row"><ZoneCell zone={r.code} /></th><td>{runs ? r.n : '…'}</td></tr>)}</tbody>
          </table>
          <div className="alerts-filters">
            <div className="alerts-controls">
              <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="alerts-zone-filter" />
              <button type="button" className="alerts-fpill" onClick={() => void loadHistory()}>Refresh</button>
            </div>
          </div>
          {runs === null ? (
            <div role="status" aria-live="polite" data-testid="tb-history-loading">
              <div className="tb-skeleton" aria-hidden="true" /><div className="tb-skeleton" aria-hidden="true" />
              <p className="tb-muted">Loading recent tests from every zone…</p>
            </div>
          ) : listed.length === 0 ? (
            <p className="tb-muted" data-testid="tb-history-empty">{all.length === 0 ? 'No tests yet. Enter a website above and press Run.' : 'No tests in this zone.'}</p>
          ) : (
            <table className="alerts-table tb-history" data-testid="tb-history">
              <colgroup><col className="tb-h-when" /><col className="tb-h-zone" /><col /><col className="tb-h-who" /><col className="tb-h-slot" /><col className="tb-h-slot" /><col className="tb-h-diff" /><col className="tb-h-actions" /></colgroup>
              <thead>
                <tr><th scope="col">Started</th><th scope="col">Zone</th><th scope="col">Website</th><th scope="col">Who</th><th scope="col">Live</th><th scope="col">Candidate</th><th scope="col">Differences</th><th scope="col">Actions</th></tr>
              </thead>
              <tbody>
                {listed.map((r) => (
                  <tr key={`${r.zone}|${r.runId}`} data-testid="tb-history-row" data-status={r.status} className={shown?.runId === r.runId ? 'tb-row-open' : undefined}>
                    <td><InstantTime iso={r.startedAt} /></td>
                    <td><ZoneCell zone={r.zone ?? HOME_ZONE.code} /></td>
                    <td className="tb-ellipsis" title={r.input.url}>{hostOf(r.input.url)} <span className="tb-muted">· {VIEWPORT_LABEL[r.input.viewports]}</span></td>
                    <td className="tb-ellipsis" title={r.startedBy.email}>{r.startedBy.email || '—'}</td>
                    <td>{r.status === 'running' ? <span className="tb-pill tb-pill--wait">Running…</span> : r.status === 'failed' && !r.live ? <span className="tb-pill tb-pill--fail">Did not finish</span> : <PassPill slot={r.live} />}</td>
                    <td>{r.status === 'running' ? <span className="tb-pill tb-pill--wait">Running…</span> : r.status === 'failed' && !r.candidate ? <span className="tb-pill tb-pill--fail">Did not finish</span> : <PassPill slot={r.candidate} />}</td>
                    <td>{r.status === 'complete' ? (r.differences.length ? <span className="tb-diff-count">{r.differences.length}</span> : 'None') : '—'}</td>
                    <td>
                      <ActionsRow>
                        <ActionIcon act="view" icon="view" label={`Open the test of ${hostOf(r.input.url)}`} tip="Open — both engines' answers side by side" onClick={() => void openRun(r)} testId="tb-open" />
                      </ActionsRow>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
